import { Injectable } from '@angular/core';
import {
    Account,
    Cost,
    Expense,
    ExpensesByAccountCategory,
    ExpensesByCategoryAccountCosts,
} from 'app/core/interfaces/reports';
import { ReportExpenses } from '../store/report.model';

@Injectable({
    providedIn: 'root',
})
export class ReportAdapterService {
    // -----------------------------------------------------------------------------
    // @ 1. Summary Adapter
    // -----------------------------------------------------------------------------

    adaptSummary(data: any) {
        // Data sample
        // {
        // 	attendance: 3442
        // 	exp_per_cap: 19.447414294015108
        // 	gross_per_cap: 90.86955258570599
        // 	profit_per_cap: 71.42213829169089
        // 	revenue: {box_office: 3783, midway: 3875, concession: 3908, novelties: 393, parking: 300450, …}
        // 	total_expenses: 66938
        // 	total_gross_sales: 312773
        // }

        const revenue = data.revenue;
        return {
            attendance: data.attendance,
            expPerCap: data.exp_per_cap,
            grossPerCap: data.gross_per_cap,
            profitPerCap: data.profit_per_cap,
            revenue: {
                boxOffice: revenue.box_office,
                midway: revenue.midway,
                concession: revenue.concession,
                novelties: revenue.novelties,
                parking: revenue.parking,
                others: revenue.others,
                total:
                    revenue.box_office +
                    revenue.midway +
                    revenue.concession +
                    revenue.novelties +
                    revenue.parking +
                    revenue.others,
            },
            totalExpenses: data.total_expenses,
            totalGrossSales: data.total_gross_sales,
        };
    }

    // -----------------------------------------------------------------------------
    // @ 2. Show Expenses Adapters
    // -----------------------------------------------------------------------------

    adaptShowExpenses(data: any): ReportExpenses {
        // Data sample
        // {
        //  expenses_by_category_accounts_and_costs: (1) [{…}]
        // 	total_show_expenses: 450
        // }
        // const expenses = data.show_expenses_by_accounts.map((item: any) =>
        //     this.adaptSingleShowExpense(item)
        // );
        // const byCategory = data.total_expenses_by_account_category.map(
        //     (item: any) => this.adaptSingleShowExpenseByCategory(item)
        // );
        const byCategoryAccountCosts =
            data.expenses_by_category_accounts_and_costs.map((item: any) =>
                this.adaptSingleExpenseByCategoryAccountCosts(item)
            );
        const total = Number(data.total_show_expenses.toFixed(2));

        // console.log({
        //     showExpenses,
        //     showExpensesByCategory,
        //     totalShowExpenses,
        // });

        return {
            expenses: null,
            expensesByCategory: null,
            expensesByCategoryAccountCosts: byCategoryAccountCosts,
            total: total,
        };
    }

    // -----------------------------------------------------------------------------
    // @ 3. Office Expenses Adapters
    // -----------------------------------------------------------------------------

    adaptOfficeExpenses(data: any): ReportExpenses {
        // Data sample
        // {
        //  expenses_by_category_accounts_and_costs: (8) [{…}]
        // 	total_office_expenses: 27437
        // }
        // const expenses = data.expenses_by_accounts.map((item: any) =>
        //     this.adaptSingleOfficeExpense(item)
        // );
        // const byCategory = data.total_by_account_categories.map((item: any) =>
        //     this.adaptSingleOfficeExpenseByCategory(item)
        // );
        const byCategoryAccountCosts =
            data.expenses_by_category_accounts_and_costs.map((item: any) =>
                this.adaptSingleExpenseByCategoryAccountCosts(item)
            );
        const total = Number(data.total_office_expenses.toFixed(2));

        return {
            expenses: null,
            expensesByCategory: null,
            expensesByCategoryAccountCosts: byCategoryAccountCosts,
            total: total,
        };
    }

    // -----------------------------------------------------------------------------
    // @ 4. Onsite Expenses Adapters
    // -----------------------------------------------------------------------------

    adaptOnsiteExpenses(data: any): ReportExpenses {
        // Data sample
        // {
        //  expenses_by_category_accounts_and_costs: (5) [{…}]
        // 	total_onsite_expenses: 31885
        // }
        // const expenses = data.expenses_by_accounts.map((item: any) =>
        //     this.adaptSingleOnsiteExpense(item)
        // );
        // const byCategory = data.total_by_account_categories.map((item: any) =>
        //     this.adaptSingleOnsiteExpenseByCategory(item)
        // );
        const byCategoryAccountCosts =
            data.expenses_by_category_accounts_and_costs.map((item: any) =>
                this.adaptSingleExpenseByCategoryAccountCosts(item)
            );
        const total = Number(data.total_onsite_expenses.toFixed(2));

        return {
            expenses: null,
            expensesByCategory: null,
            expensesByCategoryAccountCosts: byCategoryAccountCosts,
            total: total,
        };
    }

    // -----------------------------------------------------------------------------
    // @ Single Adapters
    // -----------------------------------------------------------------------------

    private adaptSingleShowExpense(data: any): Expense {
        // Data sample
        // {
        // 	account_id:	83
        // 	account_name:	"Deposit"
        // 	total:	450
        // }
        // Data sample Old
        // {
        // 	account_id:	83
        // 	account_name:	"Deposit"
        // 	category_id:	13
        // 	category_name:	"Show Expenses"
        // 	total_cost:	450
        // }

        return {
            id: data.account_id,
            accountName: data.account_name,
            categoryId: data.category_id,
            categoryName: data.category_name,
            total: Number(data.total_cost),
        };
    }

    private adaptSingleShowExpenseByCategory(
        data: any
    ): ExpensesByAccountCategory {
        // Data sample
        // {
        // 	category_id: 13
        // 	category_name: "Show Expenses"
        // 	total: 450
        // }

        // This field toggles between the show reports and the other ones
        // in this case we verify the field that is available and use its value
        const total = isNaN(Number(data.total_cost))
            ? data.total_category_cost
            : data.total_cost;

        return {
            id: data.category_id,
            categoryName: data.category_name,
            total: Number(total),
        };
    }

    private adaptSingleOfficeExpense(data: any): Expense {
        // Data sample
        // {
        // 	account_id: 9
        // 	account_name: "Software’s"
        // 	total: 3333
        // }
        return {
            id: data.account_id,
            accountName: data.account_name,
            total: Number(data.total),
        };
    }

    private adaptSingleOfficeExpenseByCategory(
        data: any
    ): ExpensesByAccountCategory {
        // Data sample
        // {
        // 	category_id: 11
        // 	category_name: "Refund"
        // 	total: 7878
        // }
        return {
            id: data.category_id,
            categoryName: data.category_name,
            total: Number(data.total),
        };
    }

    private adaptSingleOnsiteExpense(data: any): Expense {
        // Data sample
        // {
        // 	account_id: 9
        // 	account_name: "Software’s"
        // 	total: 19887
        // }
        return {
            id: data.account_id,
            accountName: data.account_name,
            total: Number(data.total),
        };
    }

    private adaptSingleOnsiteExpenseByCategory(
        data: any
    ): ExpensesByAccountCategory {
        // Data sample
        // {
        // 	category_id: 4
        // 	category_name: "Fixed Assets"
        // 	total: 7878
        // }
        return {
            id: data.category_id,
            categoryName: data.category_name,
            total: Number(data.total),
        };
    }

    // -----------------------------------------------------------------------------
    // @ Single Adapters For Expenses By Category -> Accounts -> Costs
    // -----------------------------------------------------------------------------

    private adaptSingleExpenseByCategoryAccountCosts(
        data: any
    ): ExpensesByCategoryAccountCosts {
        // console.log(data);
        //     Sample data
        //      "expenses_by_category_accounts_and_costs": [
        //   {
        //     "category_id": 35,
        //     "category_name": "Insurance",
        //     "total_category": 300,
        //     "total_refund": 0,
        //     "accounts": [
        //       {
        //         "account_id": 113,
        //         "account_name": "Vehicle Insurance",
        //         "total_account": 300,
        //         "costs": [
        //           {
        //             "id": 12,
        //             "description": "Costo 2",
        //             "cost": 200
        //           },
        //           {
        //             "id": 11,
        //             "description": "Costo 1",
        //             "cost": 100
        //           }
        //         ]
        //       }
        //     ]
        //   },
        // }
        // ]
        return {
            id: data.category_id,
            categoryName: data.category_name,
            total: data.total_category,
            refund: Number(data.total_refund ?? 0),
            accounts: data.accounts.map((acc: any) => this.adaptAccounts(acc)),
        };
    }

    private adaptAccounts(data: any): Account {
        return {
            id: data.account_id,
            accountName: data.account_name,
            total: data.total_account,
            costs: data.costs.map((cost: any) => this.adaptCosts(cost)),
        };
    }

    private adaptCosts(data: any): Cost {
        return {
            id: data.id,
            description: data.description,
            cost: data.cost,
        };
    }

    // -----------------------------------------------------------------------------
    // @  Report Adapter
    // -----------------------------------------------------------------------------

    adaptInventorySalesAnalysisReport(data: any) {
        const enrichedSubcats = data.subcategories.map((subcat: any) => {
            // Add category and subcategory to each products
            const enrichedProducts = subcat.products.map((product: any) => ({
                ...product,
                category: subcat.category,
                subcategory: {
                    id: subcat.id,
                    name: subcat.name,
                },
            }));

            // Replace old products
            const enrichedSubcat = {
                ...subcat,
                products: enrichedProducts,
            };

            return enrichedSubcat;
        });

        const mappedData = {
            total_gross: data.total_gross,
            total_turning: data.total_turning,
            subcategories: enrichedSubcats,
        };

        return mappedData;
    }

    adaptFinalReport(data: any) {
        return data;
    }

    adaptCostAnalysisReport(data: any) {
        return data;
    }
}
